import {
  VIEWER_MODE_BOUNDING_BOX,
  VIEWER_MODE_LANDSCAPE,
  VIEWER_MODE_PORTRAIT,
} from "../consts.js";

/* eslint-disable @typescript-eslint/naming-convention */
const DEFAULT_BOUNDING_BOX = {
  percentages: {
    top_left_offset: [0, 0],
    bottom_right_offset: [0, 0],
    width: 100,
    height: 100,
  },
};
/* eslint-enable @typescript-eslint/naming-convention */

export default class BoundingBoxHandler {
  boxes = [];
  margins = [];
  constructor(spinner, boundingBoxes) {
    this.spinner = spinner;
    this.boundingBoxes = boundingBoxes ?? [DEFAULT_BOUNDING_BOX];

    for (const box of this.boundingBoxes) {
      const percentages = box.percentages;
      const left = percentages.top_left_offset[0];
      const top = percentages.top_left_offset[1];
      const right = percentages.bottom_right_offset[0];
      const bottom = percentages.bottom_right_offset[1];
      const b = {
        camera: box.camera,
        left,
        top,
        right,
        bottom,
        widthRatio: 1 + (left + right) / percentages.width,
        heightRatio: 1 + (top + bottom) / percentages.height,
      };

      this.boxes.push(b);
    }
  }

  get leadImageRect() {
    return this.spinner.leadImageBoundingRect;
  }

  get spinnerRect() {
    return this.spinner.spinnerBoundingRect;
  }

  get viewerMode() {
    return this.spinner.viewerMode;
  }

  /**
   * Return contained dimensions based on the image and spinner dimension.
   * Contained dimensions mean that image will fit inside the spinner
   * without cropping.
   * @returns {{width: number, height: number}}
   */
  generateContainedDimensions() {
    const leadImage = this.leadImageRect;
    const spinner = this.spinnerRect;
    const imgAspect = leadImage.width / leadImage.height;

    const containerWidth = spinner.width;
    const containerHeight = spinner.height;

    const containerAspect = containerWidth / containerHeight;

    let newWidth, newHeight;

    if (imgAspect > containerAspect) {
      // Image is wider than container: fit to width
      newWidth = containerWidth;
      newHeight = newWidth / imgAspect;
    } else {
      // Image is taller than container: fit to height
      newHeight = containerHeight;
      newWidth = newHeight * imgAspect;
    }

    /**
     * Below code makes sure that the legacy implementation of
     * viewer modes is preserved. This calculation used to be done by css
     */
    if (this.viewerMode === VIEWER_MODE_LANDSCAPE) {
      // Width should be full container width
      newWidth = containerWidth;
      newHeight = newWidth / imgAspect;
    } else if (this.viewerMode === VIEWER_MODE_PORTRAIT) {
      /*
       * Height should be full container height, but width should never be
       * smaller than container width
       */
      newHeight = containerHeight;
      newWidth = newHeight * imgAspect;

      if (newWidth < containerWidth) {
        newWidth = containerWidth;
        newHeight = newWidth / imgAspect;
      }
    }

    return {
      width: newWidth,
      height: newHeight,
    };
  }

  getBoundingBoxDimension(widthRatio, heightRatio) {
    const { width, height } = this.generateContainedDimensions();
    const w = width * widthRatio;
    const h = height * heightRatio;

    return {
      width: w,
      height: h,
    };
  }

  getActiveBox() {
    if (this.spinner.viewerMode !== VIEWER_MODE_BOUNDING_BOX) {
      return {
        left: 0,
        top: 0,
        right: 0,
        bottom: 0,
        widthRatio: 1,
        heightRatio: 1,
      };
    }

    const rowIndex = this.spinner.activeRow - 1;

    if (rowIndex < 0 || rowIndex >= this.boxes.length) {
      return this.boxes[0];
    }

    return this.boxes[rowIndex];
  }

  /**
   *
   * @returns {{width: number, height: number, x: number, y: number}}
   */
  getTargetSize() {
    const rowBoundingBox = this.getActiveBox();
    const { width, height } = this.getBoundingBoxDimension(
      rowBoundingBox.widthRatio,
      rowBoundingBox.heightRatio,
    );

    return {
      width,
      height,
      x: this.leadImageRect.x,
      y: this.leadImageRect.y,
    };
  }

  /**
   *
   * @returns {{top, bottom, left, right}}
   */
  getValues() {
    const rowBoundingBox = this.getActiveBox();

    return {
      top: -rowBoundingBox.top / 100,
      bottom: -rowBoundingBox.bottom / 100,
      left: -rowBoundingBox.left / 100,
      right: -rowBoundingBox.right / 100,
    };
  }

  getAsCss(width, height) {
    const { top, bottom, left, right } = this.getValues();

    return {
      marginTop: `${top * height}px`,
      marginBottom: `${bottom * height}px`,
      marginLeft: `${left * width}px`,
      marginRight: `${right * width}px`,
    };
  }
}
